import React, { useEffect, useState, useRef } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Stack,
  Grid,
  Button,
  CircularProgress,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Icon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Collapse,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AIAssessmentButton from './components/AIAssessmentButton';
import Localization from '../../Localization';

// Define score bucket ranges for the histogram
const SCORE_BUCKETS = 10; // Number of buckets (0-10, 11-20, 21-30, etc.)

// The three views of the distribution chart.
const CHART_TAB_SCORE = 'score';        // each student's overall AI percentage
const CHART_TAB_PAPER = 'paper';        // each student's total marks for the paper
const CHART_TAB_TOPIC = 'topic';        // per content type

// Above this many distinct marks, total-mark bars are grouped into ranges
// instead of getting one bar each.
const MAX_DISCRETE_MARK_BARS = 12;

const StatisticCard = ({ icon, title, value, suffix }) => (
  <Card elevation={1} sx={{ 
    height: '48px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: '6px',
    overflow: 'hidden',
    border: '1px solid #e8e8e8',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(173, 17, 121, 0.08)',
      borderColor: '#ad1179'
    }
  }}>
    {/* Left side - Title */}
    <Box sx={{ 
      bgcolor: '#f5f6f7', 
      height: '100%',
      py: 1.5, 
      px: 2,
      borderRight: '1px solid #e8e8e8',
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      minWidth: '65%',
      maxWidth: '65%'
    }}>
      <Icon sx={{ color: '#ad1179', fontSize: '1rem' }}>{icon}</Icon>
      <Typography variant="body2" color="text.primary" sx={{ 
        fontWeight: '500',
        fontSize: '0.75rem',
        lineHeight: 1.2,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}>
        {title}
      </Typography>
    </Box>
    
    {/* Right side - Data */}
    <Box sx={{ 
      height: '100%',
      px: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexGrow: 1,
      bgcolor: 'white'
    }}>
      <Typography variant="h6" component="div" sx={{ 
        fontWeight: '600',
        color: '#ad1179',
        fontSize: '1.1rem'
      }}>
        {value}
      </Typography>
      {suffix && (
        <Typography variant="caption" color="text.secondary" sx={{ 
          ml: 0.5,
          fontSize: '0.7rem',
          fontWeight: '500'
        }}>
          {suffix}
        </Typography>
      )}
    </Box>
  </Card>
);


const AIAssessmentPage = ({ isAIProgressNotification, epackName, submissionDate, dateFilterStart, dateFilterEnd, isDateRangeFilter, selectedDates, submissionList, onSelectStudent, hasSubmissions, root, localeId, schoolId, tempDateFilterStart, tempDateFilterEnd, onDateFilterStartChange, onDateFilterEndChange, onConfirmDateFilter, onClearDateFilter, onExportAllAnswers, onExportAiScores, onExportMarks, canExportAnswers, isManualAIAssessment, canManageLocale = true }) => {
  console.log('DEBUG AIAssessmentPage props:', {
    submissionDate,
    dateFilterStart,
    dateFilterEnd,
    isDateRangeFilter,
    selectedDates,
    selectedDatesLength: selectedDates ? selectedDates.length : 0,
    submissionListLength: submissionList ? submissionList.length : 0,
    hasSubmissions,
    localeId,
    schoolId,
    isAIProgressNotification
  });

  // Detect timeline multi-date selection (Ctrl+click selection on the timeline)
  const hasMultipleSelectedDates = Array.isArray(selectedDates) && selectedDates.length > 1;

  const [totalAnswers, setTotalAnswers] = useState(0);
  const [assessedAnswers, setAssessedAnswers] = useState(0);
  const [studentCount, setStudentCount] = useState(0);
  const [submissionsPerStudent, setSubmissionsPerStudent] = useState(0);
  const [questionsPerStudent, setQuestionsPerStudent] = useState(0);
  const [answersDetail, setAnswersDetail] = useState(null);
  const [isAssessmentInProgress, setIsAssessmentInProgress] = useState(false);
  const [isQueueingAssessment, setIsQueueingAssessment] = useState(false);
  // Accurate count of answers still needing AI assessment for manual locales (covers voice
  // kind 13 + every submission of the date); null = unknown/not-yet-fetched. Drives the
  // "Run AI Assessment" button's disabled state. See count_pending_manual_ai_assessment_by_epack_and_date.
  const [pendingManualCount, setPendingManualCount] = useState(null);
  const [progressDetails, setProgressDetails] = useState(null);
  const [batchStartTime, setBatchStartTime] = useState(null);
  const [isResumedAssessment, setIsResumedAssessment] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const [failureCount, setFailureCount] = useState(0);
  const [scoreBuckets, setScoreBuckets] = useState(Array(SCORE_BUCKETS).fill(0));
  const [bucketStudents, setBucketStudents] = useState(Array(SCORE_BUCKETS).fill(null).map(() => []));
  // The 得分 tab is one bar-count per STUDENT (their overall percentage), which is
  // NOT what scoreBuckets holds — that one is one datum per ANSWER, and it still
  // feeds averageScore and the performance summary below.
  const [studentScoreBuckets, setStudentScoreBuckets] = useState(Array(SCORE_BUCKETS).fill(0));
  const [studentBucketMembers, setStudentBucketMembers] = useState(Array(SCORE_BUCKETS).fill(null).map(() => []));
  const [studentAverageScore, setStudentAverageScore] = useState(0);
  // null until the user picks a view, so the default can follow the data.
  const [chartTab, setChartTab] = useState(null);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [hasScoreData, setHasScoreData] = useState(false);
  const [averageScore, setAverageScore] = useState(0);
  const [topPerformingQuestions, setTopPerformingQuestions] = useState([]);
  const [topPerformingStudents, setTopPerformingStudents] = useState([]);
  const [lowPerformingQuestions, setLowPerformingQuestions] = useState([]);
  const [lowPerformingStudents, setLowPerformingStudents] = useState([]);
  const [isPerformanceDataLoading, setIsPerformanceDataLoading] = useState(false);
  const [hasPerformanceData, setHasPerformanceData] = useState(false);
  const [showNoQuestionsDialog, setShowNoQuestionsDialog] = useState(false);
  const [isDateFilterExpanded, setIsDateFilterExpanded] = useState(false);
  const progressTimerRef = useRef(null);
  const batchStartTimeRef = useRef(null);
  const pollingStartTimeRef = useRef(null);
  const isUnmountedRef = useRef(false);

  // Polling configuration: delay between progress checks and a hard cutoff so a
  // stuck batch cannot poll the API forever.
  const PROGRESS_POLL_INTERVAL_MS = 2000;
  const PROGRESS_POLL_MAX_DURATION_MS = 10 * 60 * 1000; // 10 minutes

  // Helper function to format date based on locale
  const formatDateByLocale = (dateString) => {
    const date = new Date(dateString);
    const currentLanguage = Localization.strings.getLanguage();
    
    if (currentLanguage === 'tc') {
      // Chinese format: YYYY年M月D日
      const year = date.getFullYear();
      const month = date.getMonth() + 1; // getMonth() returns 0-11
      const day = date.getDate();
      return `${year}年${month}月${day}日`;
    } else {
      // English format: DD-MM-YYYY
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }
  };

  // Helper function to format date display
  const getSelectedDateDisplay = () => {
    // Priority 1: Check for multiple selected dates from timeline (Ctrl+click)
    if (selectedDates && selectedDates.length > 1) {
      // Multiple dates selected - show as date range
      const sortedDates = [...selectedDates].sort((a, b) => new Date(a) - new Date(b));
      const startDate = formatDateByLocale(sortedDates[0]);
      const endDate = formatDateByLocale(sortedDates[sortedDates.length - 1]);
      return `${startDate} - ${endDate}`;
    }
    
    // Priority 2: Check for date range filter
    if (isDateRangeFilter && dateFilterStart && dateFilterEnd) {
      // Date range from filter
      const startDate = formatDateByLocale(dateFilterStart);
      const endDate = formatDateByLocale(dateFilterEnd);
      return `${startDate} - ${endDate}`;
    } else if (isDateRangeFilter && (dateFilterStart || dateFilterEnd)) {
      // Single date from filter
      const date = dateFilterStart || dateFilterEnd;
      return formatDateByLocale(date);
    }
    
    // Priority 3: Check for single selected date from timeline
    if (selectedDates && selectedDates.length === 1) {
      return formatDateByLocale(selectedDates[0]);
    }
    
    // Priority 4: Check for single submission date
    if (submissionDate) {
      return formatDateByLocale(submissionDate);
    }
    
    return 'No Date Selected';
  };

  const formattedDate = isDateRangeFilter && dateFilterStart && dateFilterEnd ? 
    `${new Date(dateFilterStart).toLocaleDateString('zh-HK')} - ${new Date(dateFilterEnd).toLocaleDateString('zh-HK')}` :
    (submissionDate ? new Date(submissionDate).toLocaleDateString('zh-HK') : '-');

  // Function to calculate totals from submissionList for date range filters
  const calculateTotalsFromSubmissionList = () => {
    console.log('DEBUG: calculateTotalsFromSubmissionList called with', submissionList.length, 'submissions');
    console.log('DEBUG: Sample submission structure:', submissionList[0]);
    
    // Calculate unique students (key on the student identifier, not the per-submission id,
    // to match OverallAssessmentPage and the score aggregation and avoid inflating the count).
    const uniqueStudents = new Set(submissionList.map(submission => submission.user_name));
    const calculatedStudentCount = uniqueStudents.size;
    console.log('DEBUG: Calculated student count:', calculatedStudentCount);
    setStudentCount(calculatedStudentCount);
    
    // We need to get the actual answers from the API for each submission
    // The submissionList contains submission metadata, but we need to fetch the actual answers
    console.log('DEBUG: Need to fetch answers for', submissionList.length, 'submissions');
    
    // For now, let's use a different approach - make API calls for each unique date
    const uniqueDates = (!isDateRangeFilter && hasMultipleSelectedDates)
      ? [...new Set(selectedDates.map(date => {
          if (typeof date === 'string') {
            return date.split(' ')[0];
          }
          return null;
        }).filter(Boolean))]
      : [...new Set(submissionList.map(submission => submission.submission_time.split(' ')[0]))];
    console.log('DEBUG: Unique dates in submissions:', uniqueDates);
    
    // Call APIs for each unique date and aggregate the results
    fetchAggregatedDataForDates(uniqueDates);
  };

  // Function to fetch and aggregate data from multiple dates
  const fetchAggregatedDataForDates = (dates) => {
    console.log('DEBUG: fetchAggregatedDataForDates called with dates:', dates);
    
    let completedRequests = 0;
    let aggregatedTotalAnswers = 0;
    let aggregatedAssessedAnswers = 0;
    
    dates.forEach(date => {
      const params = {
        locale_id: localeId,
        date: date,
        school_id: schoolId
      };
      
      console.log('DEBUG: Making aggregated API calls for date:', date);
      
      // Get total answers for this date
      root.refs.API.request(
        "get_total_answers_number_by_epack_and_date",
        params,
        (res) => {
          const dateTotal = parseInt((res && res.body && res.body['result'] && res.body['result'].total) || 0);
          aggregatedTotalAnswers += dateTotal;
          console.log('DEBUG: Date', date, 'total answers:', dateTotal, 'Running total:', aggregatedTotalAnswers);
          
          // Get assessed answers for this date
          root.refs.API.request(
            "get_total_assessed_answers_number_by_epack_and_date",
            params,
            (res) => {
              const dateAssessed = parseInt((res && res.body && res.body['result'] && res.body['result'].total) || 0);
              aggregatedAssessedAnswers += dateAssessed;
              console.log('DEBUG: Date', date, 'assessed answers:', dateAssessed, 'Running total:', aggregatedAssessedAnswers);
              
              completedRequests++;
              
              // When all dates are processed, update the state
              if (completedRequests === dates.length) {
                console.log('DEBUG: All dates processed. Final totals - Answers:', aggregatedTotalAnswers, 'Assessed:', aggregatedAssessedAnswers);
                setTotalAnswers(aggregatedTotalAnswers);
                setAssessedAnswers(aggregatedAssessedAnswers);
              }
            },
            (err) => {
              console.log('ERROR: Failed to get assessed answers for date:', date);
              completedRequests++;
              if (completedRequests === dates.length) {
                setTotalAnswers(aggregatedTotalAnswers);
                setAssessedAnswers(aggregatedAssessedAnswers);
              }
            }
          );
        },
        (err) => {
          console.log('ERROR: Failed to get total answers for date:', date);
          completedRequests++;
          if (completedRequests === dates.length) {
            setTotalAnswers(aggregatedTotalAnswers);
            setAssessedAnswers(aggregatedAssessedAnswers);
          }
        }
      );
    });
  };

  // Function to extract score from AI assessment text
  const extractScoreFromAssessment = (assessmentText) => {
    if (!assessmentText) return 0;
    
    // Handle special cases
    if (assessmentText.trim() === "No answer provided" || assessmentText.trim() === "") {
      return 0; // Count blank or "No answer provided" as 0%
    }
    
    // Look for percentage pattern like <40％> or <50%> or 正確程度：<50％>
    const percentageMatch = assessmentText.match(/<(\d+)%?>|<(\d+)％>|正確程度：<(\d+)[%％]>|正確程度評分：<(\d+)[%％]>/);
    if (percentageMatch) {
      // Find the first non-undefined capturing group
      const score = parseInt(
        percentageMatch[1] || percentageMatch[2] || percentageMatch[3] || percentageMatch[4], 
        10
      );
      return score;
    }
    
    // Look for other formats like "Score: 50%" or "正確程度: 50%"
    const scoreMatch = assessmentText.match(/Score:\s*(\d+)[%％]|正確程度[:：]\s*(\d+)[%％]/i);
    if (scoreMatch) {
      const score = parseInt(scoreMatch[1] || scoreMatch[2], 10);
      return score;
    }
    
    console.log("Could not extract score from:", assessmentText.substring(0, 100) + "...");
    return 0; // Default to 0 if no score pattern is found
  };

  // Function to group scores into buckets and track students in each bucket
  const groupScoresIntoBuckets = (scores, studentData) => {
    const buckets = Array(SCORE_BUCKETS).fill(0);
    const studentBuckets = Array(SCORE_BUCKETS).fill(null).map(() => []);
    
    // Process each score and track which student it belongs to
    scores.forEach((score, index) => {
      // Ensure score is a valid number
      if (typeof score === 'number' && !isNaN(score)) {
        const studentInfo = studentData[index];
        let bucketIndex;
        
        // Handle special case for score = 100, put it in the last bucket
        if (score === 100) {
          bucketIndex = SCORE_BUCKETS - 1;
          buckets[bucketIndex]++;
          
          // Add student to this bucket if not already included
          if (studentInfo && !studentBuckets[bucketIndex].some(s => s.name === studentInfo.name)) {
            studentBuckets[bucketIndex].push(studentInfo);
          }
        } 
        // Handle scores from 0-99
        else {
          // Determine which bucket the score belongs to (0-9, 10-19, etc.)
          bucketIndex = Math.floor(score / 10);
          if (bucketIndex >= 0 && bucketIndex < SCORE_BUCKETS) {
            buckets[bucketIndex]++;
            
            // Add student to this bucket if not already included
            if (studentInfo && !studentBuckets[bucketIndex].some(s => s.name === studentInfo.name)) {
              studentBuckets[bucketIndex].push(studentInfo);
            }
          } else {
            console.warn(`Score ${score} is outside expected range 0-100, not included in histogram`);
          }
        }
      } else {
        console.warn(`Invalid score value: ${score}, type: ${typeof score}`);
      }
    });
    
    return { buckets, studentBuckets };
  };

  // Function to fetch all AI assessment responses
  const fetchAllAIAssessments = () => {
    if (!localeId || !submissionDate || !schoolId) {
      console.error('Missing required parameters for fetchAllAIAssessments');
      return;
    }
    
    setIsChartLoading(true);
    
    const shouldAggregateDates = (isDateRangeFilter || hasMultipleSelectedDates) && submissionList && submissionList.length > 0;

    if (shouldAggregateDates) {
      // For date range filters, fetch and aggregate AI assessment data from multiple dates
      console.log('DEBUG: Fetching AI assessment data for date range filter');
      fetchAggregatedAIAssessmentData();
    } else {
      // For single date, use existing logic
      console.log('DEBUG: Fetching AI assessment data for single date');
      // Use the actual submission date for API calls
    let dateParam = submissionDate;
      
      // Format date correctly if it contains time
    if (!dateParam.includes(' ')) {
      dateParam = `${dateParam} 00:00:00`;
    }
    
    const params = {
      locale_id: localeId,
      date: dateParam,
      school_id: schoolId
    };
    
    console.log('Fetching AI assessment data with params:', params);
    
    root.refs.API.request(
      "get_all_ai_assessment_response",
      params,
      handleAIAssessmentDataSuccess,
      handleAIAssessmentDataError
    );
    }
  };

  // Function to fetch and aggregate AI assessment data from multiple dates
  const fetchAggregatedAIAssessmentData = () => {
    const uniqueDates = (!isDateRangeFilter && hasMultipleSelectedDates)
      ? [...new Set(selectedDates.map(date => {
          if (typeof date === 'string') {
            return date.split(' ')[0];
          }
          return null;
        }).filter(Boolean))]
      : [...new Set(submissionList.map(submission => 
          submission.submission_time.split(' ')[0]
        ))];
    console.log('DEBUG: Fetching AI assessment data for dates:', uniqueDates);
    
    let completedRequests = 0;
    let aggregatedAssessmentData = [];
    
    uniqueDates.forEach(date => {
      const dateParam = `${date} 00:00:00`;
      const params = {
        locale_id: localeId,
        date: dateParam,
        school_id: schoolId
      };
      
      console.log('DEBUG: Fetching AI assessment data for date:', date);
      
      root.refs.API.request(
        "get_all_ai_assessment_response",
        params,
        (res) => {
          console.log('DEBUG: AI assessment data for date', date, ':', res.body);
          
          if (res.body && res.body.result && Array.isArray(res.body.result)) {
            // Add date information to each student's data for tracking
            const dateSpecificData = res.body.result.map(student => ({
              ...student,
              sourceDate: date
            }));
            aggregatedAssessmentData = aggregatedAssessmentData.concat(dateSpecificData);
          }
          
          completedRequests++;
          
          // When all requests are complete, process the aggregated data
          if (completedRequests === uniqueDates.length) {
            console.log('DEBUG: All AI assessment data fetched. Total students:', aggregatedAssessmentData.length);
            
            // Create a response object that matches the expected format
            const aggregatedResponse = {
              body: {
                result: aggregatedAssessmentData,
                count: aggregatedAssessmentData.length,
                cmd: 'get_all_ai_assessment_response',
                remarks: 'Aggregated from multiple dates'
              }
            };
            
            handleAIAssessmentDataSuccess(aggregatedResponse);
          }
        },
        (err) => {
          console.log('ERROR: Failed to fetch AI assessment data for date:', date);
          completedRequests++;
          
          if (completedRequests === uniqueDates.length) {
            // Process whatever data we have
            const aggregatedResponse = {
              body: {
                result: aggregatedAssessmentData,
                count: aggregatedAssessmentData.length,
                cmd: 'get_all_ai_assessment_response',
                remarks: 'Aggregated from multiple dates (with some errors)'
              }
            };
            
            handleAIAssessmentDataSuccess(aggregatedResponse);
          }
        }
      );
    });
  };

  // Handle successful API response
  const handleAIAssessmentDataSuccess = (res) => {
    console.log('AI assessment data received:', res.body);
    setIsChartLoading(false);
    setIsPerformanceDataLoading(true);
    
    if (!res.body || !res.body.result || !Array.isArray(res.body.result)) {
      console.error('Invalid response format for AI assessments');
      setHasScoreData(false);
      setHasPerformanceData(false);
      setIsPerformanceDataLoading(false);
      return;
    }
    
    // Process assessment data
    const assessmentData = res.body.result;
    const allScores = [];
    const allStudentData = [];
    let totalProcessed = 0;
    
    // For performance analysis
    const studentScores = {}; // Track average scores by student
    const questionScores = {}; // Track average scores by question
    
    // Iterate through each student
    assessmentData.forEach(student => {
      console.log(`Processing assessments for student: ${student.user_name}`);
      
      if (student.assessments && Array.isArray(student.assessments)) {
        // Initialize student score tracking
        if (!studentScores[student.user_name]) {
          studentScores[student.user_name] = {
            total: 0,
            count: 0,
            average: 0
          };
        }
        
        // Extract scores from each assessment
        student.assessments.forEach(assessment => {
          totalProcessed++;

          // A question the student never answered is not a 0% performance, it
          // is an absence — counting it drags every average down and puts a
          // phantom student in the 0-9 bucket. student_answer is already
          // COALESCEd with the voice transcription server-side, so an empty
          // one really does mean nothing was submitted.
          const studentAnswer = assessment.student_answer;
          if (studentAnswer === undefined || studentAnswer === null || String(studentAnswer).trim() === '') {
            return;
          }

          const score = extractScoreFromAssessment(assessment.ai_assessment);

          // Extract a truncated question for display
          const questionText = assessment.question || '';
          const truncatedQuestion = questionText.length > 50 
            ? questionText.substring(0, 50) + '...' 
            : questionText;
          
          // Update question scores
          if (!questionScores[truncatedQuestion]) {
            questionScores[truncatedQuestion] = {
              text: truncatedQuestion,
              total: 0,
              count: 0,
              average: 0,
              fullText: questionText
            };
          }
          questionScores[truncatedQuestion].total += score;
          questionScores[truncatedQuestion].count += 1;
          
          // Update student scores
          studentScores[student.user_name].total += score;
          studentScores[student.user_name].count += 1;
          
          // Log the extracted score for debugging
          console.log(`Student: ${student.user_name} | Question: ${questionText.substring(0, 30)}... | Score: ${score}`);
          
          // Track the score and which student it belongs to
          allScores.push(score);
          allStudentData.push({
            name: student.user_name,
            question: assessment.question,
            questionText: truncatedQuestion,
            answer: assessment.student_answer,
            score: score
          });
        });
      } else {
        console.warn(`No assessments found for student: ${student.user_name}`);
      }
    });
    
    console.log(`Processed ${totalProcessed} total assessments`);
    console.log(`Extracted ${allScores.length} scores from assessments`);
    
    if (allScores.length > 0) {
      // Group scores into buckets and track students in each bucket
      const { buckets, studentBuckets } = groupScoresIntoBuckets(allScores, allStudentData);
      console.log('Score distribution buckets:', buckets);
      console.log('Students in each bucket:', studentBuckets);
      
      // Update state with bucket data
      setScoreBuckets(buckets);
      setBucketStudents(studentBuckets);
      setHasScoreData(true);
      
      // Calculate average score
      const totalScore = allScores.reduce((acc, curr) => acc + curr, 0);
      const avgScore = Math.round((totalScore / allScores.length) * 10) / 10; // Round to 1 decimal place
      setAverageScore(avgScore);
      
      // Skipping unanswered questions above means a student (or a question) can
      // end up with nothing counted at all. count === 0 would make these NaN and
      // put a "NaN%" in the average pill, so those entries are dropped outright
      // — a student who answered nothing has no score, not a zero.
      Object.keys(questionScores).forEach(key => {
        const questionData = questionScores[key];
        if (questionData.count === 0) {
          delete questionScores[key];
          return;
        }
        questionData.average = Math.round((questionData.total / questionData.count) * 10) / 10;
      });

      Object.keys(studentScores).forEach(key => {
        const studentData = studentScores[key];
        if (studentData.count === 0) {
          delete studentScores[key];
          return;
        }
        studentData.average = Math.round((studentData.total / studentData.count) * 10) / 10;
      });

      // The 得分 tab counts STUDENTS, not answers: bucket each student's own
      // average so one bar of "3" means three students, not three answers.
      const studentAverages = Object.keys(studentScores).map(name => studentScores[name].average);
      const studentRows = Object.keys(studentScores).map(name => ({ name }));
      const perStudent = groupScoresIntoBuckets(studentAverages, studentRows);
      setStudentScoreBuckets(perStudent.buckets);
      setStudentBucketMembers(perStudent.studentBuckets);
      setStudentAverageScore(
        studentAverages.length > 0
          ? Math.round((studentAverages.reduce((acc, cur) => acc + cur, 0) / studentAverages.length) * 10) / 10
          : 0
      );
      
      // Sort questions by average score
      const sortedQuestions = Object.values(questionScores).sort((a, b) => b.average - a.average);
      const topQuestions = sortedQuestions.slice(0, 3); // Top 3
      // Bottom 3 taken from the items after the top slice so they never overlap the top list
      const bottomQuestions = sortedQuestions.slice(Math.max(3, sortedQuestions.length - 3)).reverse();
      
      // Sort students by average score
      const sortedStudents = Object.entries(studentScores)
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.average - a.average);
      
      const topStudents = sortedStudents.slice(0, 3); // Top 3
      // Bottom 3 taken from the items after the top slice so they never overlap the top list
      const bottomStudents = sortedStudents.slice(Math.max(3, sortedStudents.length - 3)).reverse();
      
      // Update performance data state
      setTopPerformingQuestions(topQuestions);
      setLowPerformingQuestions(bottomQuestions);
      setTopPerformingStudents(topStudents);
      setLowPerformingStudents(bottomStudents);
      setHasPerformanceData(true);
      
      console.log('Performance data calculated:', {
        averageScore: avgScore,
        topQuestions,
        bottomQuestions,
        topStudents,
        bottomStudents
      });
    } else {
      console.warn('No valid scores found in the assessment data');
      setHasScoreData(false);
      setHasPerformanceData(false);
      setStudentScoreBuckets(Array(SCORE_BUCKETS).fill(0));
      setStudentBucketMembers(Array(SCORE_BUCKETS).fill(null).map(() => []));
      setStudentAverageScore(0);
    }
    
    setIsPerformanceDataLoading(false);
  };

  // Handle API error
  const handleAIAssessmentDataError = (err) => {
    console.error('Failed to fetch AI assessment data:', err);
    setIsChartLoading(false);
    setIsPerformanceDataLoading(false);
    setHasScoreData(false);
    setHasPerformanceData(false);
  };

  // Format tooltip content for each bucket. Names are links when the page was
  // given a way to open a student — clicking one leaves this aggregate view for
  // that student's answers.
  const formatTooltipContent = (students) => {
    if (!students || students.length === 0) return Localization.strings.NO_STUDENTS;

    // Group by student name to show each student only once
    const uniqueStudents = {};
    students.forEach(student => {
      if (!uniqueStudents[student.name]) {
        uniqueStudents[student.name] = { name: student.name, count: 1 };
      } else {
        uniqueStudents[student.name].count++;
      }
    });

    const rows = Object.values(uniqueStudents)
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));

    return (
      <Box>
        {rows.map((student) => {
          const text = `${student.name}${student.count > 1 ? ` (${student.count} answers)` : ''}`;
          if (!onSelectStudent) {
            return (
              <Typography key={student.name} variant="caption" sx={{ display: 'block' }}>
                {text}
              </Typography>
            );
          }
          return (
            <Typography
              key={student.name}
              variant="caption"
              // stopPropagation: the name sits inside a bar's tooltip, and the
              // click is a navigation, not a chart interaction.
              onClick={(e) => { e.stopPropagation(); onSelectStudent(student.name); }}
              sx={{
                display: 'block',
                cursor: 'pointer',
                color: '#ad1179',
                textDecoration: 'underline',
                '&:hover': { opacity: 0.7 },
              }}
            >
              {text}
            </Typography>
          );
        })}
      </Box>
    );
  };

  // ---------------------------------------------------------------- 試卷分
  // Paper Score needs no request: submissionList is the raw
  // get_submission_list_by_epack_and_date result and already carries score /
  // score_max. Rows carrying ai_average_score are EXCLUDED — RetrieverPage
  // rewrote those to a percentage out of a fake score_max of 100, so counting
  // them would mix percentages into a marks histogram.
  const paperRows = (submissionList || []).filter((row) => (
    row && row.ai_average_score === undefined && parseFloat(row.score) >= 0
  ));
  const paperReferenceRow = (submissionList || []).find((row) => (
    row && row.ai_average_score === undefined && parseFloat(row.score_max) >= 0
  ));
  const paperMaxMarks = paperReferenceRow ? parseFloat(paperReferenceRow.score_max) : NaN;
  // score_max is -1 when no component in the LOCALE has marks assigned, and a
  // maximum of 0 would give a zero-width axis — neither can be charted.
  const canShowPaperScore = !isNaN(paperMaxMarks) && paperMaxMarks > 0;

  /** Histogram of total marks. One bar per mark while that stays readable. */
  const buildMarkBuckets = (rows, maxMarks) => {
    const discrete = maxMarks + 1 <= MAX_DISCRETE_MARK_BARS;
    const bucketCount = discrete ? maxMarks + 1 : SCORE_BUCKETS;
    const width = discrete ? 1 : maxMarks / SCORE_BUCKETS;
    const buckets = Array(bucketCount).fill(0);
    const members = Array(bucketCount).fill(null).map(() => []);
    const labels = Array(bucketCount).fill('');

    for (let i = 0; i < bucketCount; i += 1) {
      if (discrete) {
        labels[i] = String(i);
      } else {
        const from = Math.round(i * width);
        const to = (i === bucketCount - 1) ? maxMarks : Math.round((i + 1) * width) - 1;
        labels[i] = (from >= to) ? String(from) : `${from}-${to}`;
      }
    }

    rows.forEach((row) => {
      const marks = parseFloat(row.score);
      if (isNaN(marks) || marks < 0) return;
      // A full-marks paper belongs in the last bar, not one past the end.
      const index = (marks >= maxMarks)
        ? bucketCount - 1
        : Math.min(bucketCount - 1, Math.floor(marks / width));
      buckets[index] += 1;
      members[index].push({ name: row.user_name });
    });

    return { buckets, members, labels };
  };

  const paperData = canShowPaperScore
    ? buildMarkBuckets(paperRows, paperMaxMarks)
    : { buckets: [], members: [], labels: [] };
  const paperAverageMarks = paperRows.length > 0
    ? Math.round((paperRows.reduce((acc, row) => acc + parseFloat(row.score), 0) / paperRows.length) * 10) / 10
    : 0;

  // ----------------------------------------------------------------- 題目
  // The original chart, unchanged: the same 0-9 … 90-100 bands, but one datum
  // per ANSWER rather than per student. It is the per-question view, so it
  // keeps scoreBuckets while 得分 uses the per-student buckets.
  const canShowByTopic = hasScoreData;
  const decadeLabels = Array.from({ length: SCORE_BUCKETS }).map((_, index) => (
    index === 0 ? '0-9' : index === SCORE_BUCKETS - 1 ? '90-100' : `${index * 10}-${index * 10 + 9}`
  ));

  // ------------------------------------------------------------- tab wiring
  // All three tabs are always offered so the user can switch freely; only the
  // DEFAULT follows the data. chartTab stays null until they pick one, and once
  // they have, their choice is honoured even if that view is empty — they can
  // always click back out.
  const tabHasData = (tab) => {
    if (tab === CHART_TAB_PAPER) return canShowPaperScore && paperRows.length > 0;
    if (tab === CHART_TAB_TOPIC) return canShowByTopic;
    return hasScoreData;
  };
  const chartTabs = [CHART_TAB_SCORE, CHART_TAB_PAPER, CHART_TAB_TOPIC];
  const activeChartTab = chartTab
    || chartTabs.find(tabHasData)
    || CHART_TAB_SCORE;

  // ------------------------------------------------------- score export gates
  // A score export with nothing in it is a dead button, so each one appears only
  // when the LOCALE carries that kind of figure -- the same conditions the two
  // chart tabs above use.
  //
  // AI is deliberately the more permissive of the two: assessedAnswers > 0 is
  // proof the locale HAS AI scores even when the chart request has not landed
  // yet (or failed), and a live button backed by the "nothing to export" alert
  // beats a button that is missing when it should work.
  //
  // Marks needs no such fallback -- submissionList already carries score /
  // score_max, and an AI-score sort only ANNOTATES rows that have no marks of
  // their own, so paperRows survives it.
  const localeHasAiScores = hasScoreData || assessedAnswers > 0;
  const localeHasMarks = canShowPaperScore && paperRows.length > 0;

  const isPaperTab = activeChartTab === CHART_TAB_PAPER;
  const isTopicTab = activeChartTab === CHART_TAB_TOPIC;

  const activeBuckets = isPaperTab
    ? paperData.buckets
    : (isTopicTab ? scoreBuckets : studentScoreBuckets);
  // 題目 counts answers, not people, so the per-bucket name list the 得分 tab
  // shows would be misleading there — it gets no hover detail at all.
  const activeMembers = isPaperTab
    ? paperData.members
    : (isTopicTab ? null : studentBucketMembers);
  const activeLabels = isPaperTab ? paperData.labels : decadeLabels;
  const activeAxisMax = Math.max(...activeBuckets, 1);
  const activeHasData = tabHasData(activeChartTab);
  // The bars and the x-axis ticks are two independent flex rows aligned only by
  // sharing this width — compute it once so a tab with a different bar count
  // cannot make them drift apart. 10 bars keeps the original 8%.
  const activeBarWidth = `${Math.min(16, 80 / Math.max(activeBuckets.length, 1))}%`;

  // Find the maximum value in the buckets for scaling the chart
  const maxBucketValue = Math.max(...scoreBuckets, 1);

  // Define colors for the bars
  const getBarColor = (index) => {
    // Color gradient from red (low score) to green (high score)
    const colors = [
      '#f44336', // Red (0-10)
      '#ff5722', // Deep Orange (11-20)
      '#ff9800', // Orange (21-30)
      '#ffc107', // Amber (31-40)
      '#ffeb3b', // Yellow (41-50)
      '#cddc39', // Lime (51-60)
      '#8bc34a', // Light Green (61-70)
      '#4caf50', // Green (71-80)
      '#009688', // Teal (81-90)
      '#2196f3', // Blue (91-100)
    ];
    return colors[index] || '#1976d2';
  };

  // The ramp above is indexed 0-9 by SCORE bucket; Paper Score can have a
  // different bar count, so its index is mapped proportionally.
  const getActiveBarColor = (index) => {
    if (isPaperTab) {
      const count = Math.max(activeBuckets.length, 1);
      return getBarColor(Math.min(SCORE_BUCKETS - 1, Math.floor((index / count) * SCORE_BUCKETS)));
    }
    return getBarColor(index);
  };

  /** '' suppresses the MUI tooltip entirely — see activeMembers for why 題目 has none. */
  const activeBarTooltip = (index) => (
    activeMembers ? formatTooltipContent(activeMembers[index]) : ''
  );

  // Additional useEffect to fetch assessment data when assessedAnswers changes
  useEffect(() => {
    if (assessedAnswers > 0 && localeId && submissionDate && schoolId) {
      fetchAllAIAssessments();
    }
  }, [assessedAnswers, localeId, submissionDate, schoolId, hasMultipleSelectedDates, selectedDates, isDateRangeFilter]);

  // Keep the accurate pending-answer count fresh for manual locales: on date/scope change
  // and whenever the assessed count updates (e.g. after the cron processes queued answers).
  useEffect(() => {
    if (isManualAIAssessment) {
      refreshPendingManualCount();
    } else {
      setPendingManualCount(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManualAIAssessment, localeId, submissionDate, schoolId, hasMultipleSelectedDates, selectedDates, isDateRangeFilter, submissionList, assessedAnswers]);

  const getTotalAnswersOK = (res) => {
    const result = (res && res.body && res.body['result']) || {};
    console.log('Total answers:', result);
    const total = result.total || 0;
    setTotalAnswers(total);

    // Check for answer IDs in both possible response formats
    if (result.answer_info) {
      const answerInfo = result.answer_info;
      
      // Check if we have answer_ids directly
      if (answerInfo.answer_ids && Array.isArray(answerInfo.answer_ids)) {
        console.log('Answer IDs found:', answerInfo.answer_ids.length);
        setAnswersDetail(answerInfo.answer_ids);
      } 
      // Or if we have answer_details that need to be mapped
      else if (answerInfo.answer_details && Array.isArray(answerInfo.answer_details)) {
        const answerDetails = answerInfo.answer_details;
        console.log('Answer details found:', answerDetails.length);
        
        // Extract just the answer_id values into an array
        const answerIds = answerDetails.map(detail => detail.answer_id);
        console.log('Answer IDs extracted:', answerIds);
        setAnswersDetail(answerIds);
      }
    }
  };

  const getTotalAnswersFailed = (err) => {
    console.error('Failed to get total answers:', err);
  };

  const getAssessedAnswersOK = (res) => {
    const result = (res && res.body && res.body['result']) || {};
    console.log('Assessed answers:', result);
    const assessed = result.total || 0;
    setAssessedAnswers(assessed);
    // Note: the assessment data fetch is driven by the useEffect on [assessedAnswers, ...]
    // above. Calling fetchAllAIAssessments() here as well caused two concurrent identical
    // requests to race on the same load, so it is intentionally not called here.
  };

  const getAssessedAnswersFailed = (err) => {
    console.error('Failed to get assessed answers:', err);
  };

  const getQuestionsPerStudentOK = (res) => {
    const result = (res && res.body && res.body['result']) || {};
    console.log('Questions per student:', result);
    const count = parseInt(result.count) || 0;
    setQuestionsPerStudent(count);
  };

  const getQuestionsPerStudentFailed = (err) => {
    console.error('Failed to get questions per student:', err);
  };

  // Schedule the next 2s progress poll, honouring unmount / no-batch / the
  // elapsed-time cutoff. Shared by the OK and the FAILED path: a failed poll
  // (expired token, 5xx, a transient CORS blip) must keep polling rather than
  // abandon the batch — dropping it left the spinner up forever with nothing
  // ever refreshing it.
  const scheduleNextProgressPoll = () => {
    if (isUnmountedRef.current) {
      console.log('Component unmounted, stopping progress polling');
      return;
    }

    if (!batchStartTimeRef.current) {
      console.log('Skipping next progress API call, batchStartTimeRef not set');
      return;
    }

    // Enforce an elapsed-time cutoff so a stuck batch cannot poll forever.
    if (pollingStartTimeRef.current === null) {
      pollingStartTimeRef.current = Date.now();
    }
    if (Date.now() - pollingStartTimeRef.current > PROGRESS_POLL_MAX_DURATION_MS) {
      console.warn('Assessment progress polling timed out, stopping progress tracking');
      setIsAssessmentInProgress(false);
      if (progressTimerRef.current) {
        clearTimeout(progressTimerRef.current);
        progressTimerRef.current = null;
      }
      pollingStartTimeRef.current = null;
      return;
    }

    console.log('Assessment still in progress, scheduling next progress check in', PROGRESS_POLL_INTERVAL_MS, 'ms');
    if (progressTimerRef.current) {
      clearTimeout(progressTimerRef.current);
    }
    progressTimerRef.current = setTimeout(() => {
      progressTimerRef.current = null;
      if (isUnmountedRef.current || !batchStartTimeRef.current) {
        return;
      }
      console.log('Making next progress API call with batchStartTime:', batchStartTimeRef.current);
      fetchAssessmentProgress(batchStartTimeRef.current);
    }, PROGRESS_POLL_INTERVAL_MS);
  };

  const getProgressOK = (res) => {
    const outer = (res && res.body && res.body['result']) || {};
    console.log('Assessment batch progress:', outer.result);
    const result = outer.result || {};
    
    // Update the progress details
    setProgressDetails(result);
    
    // Update the total answers and assessed answers
    if (result.total_answers !== undefined) {
      setTotalAnswers(result.total_answers);
    }
    
    if (result.completed_successfully !== undefined) {
      setAssessedAnswers(result.completed_successfully);
    }
    
    // Check if assessment is complete
    if (result.is_complete === true) {
      console.log('Assessment is complete, stopping progress tracking');
      setIsAssessmentInProgress(false);
      // Stop the timer when assessment is complete
      if (progressTimerRef.current) {
        clearTimeout(progressTimerRef.current);
        progressTimerRef.current = null;
      }
      pollingStartTimeRef.current = null;
    } else {
      // If assessment is not complete, schedule the next progress check after a
      // delay instead of recursing synchronously, so we don't hammer the API.
      scheduleNextProgressPoll();
    }
  };

  const getProgressFailed = (err) => {
    console.error('Failed to get assessment progress:', err);
    // A failed poll says nothing about the batch — keep polling (the elapsed
    // cutoff inside scheduleNextProgressPoll still bounds it).
    scheduleNextProgressPoll();
  };

  const fetchAssessmentProgress = (initialBatchStartTime) => {
    // If initialBatchStartTime is provided, use it - this handles the first call before state is updated
    const effectiveBatchStartTime = initialBatchStartTime || batchStartTimeRef.current || batchStartTime;
    
    console.log('fetchAssessmentProgress called, batchStartTime:', effectiveBatchStartTime);
    
    if (localeId && submissionDate && schoolId && effectiveBatchStartTime) {
      // Use the actual submission date for API calls
      let dateParam = submissionDate;
      
      const params = {
        locale_id: localeId,
        date: dateParam,
        school_id: schoolId,
        batch_start_time: effectiveBatchStartTime,
        _nocache: Date.now() // Add unique timestamp to force new connection
      };

      // Use different API endpoints based on whether it's a new or resumed assessment
      const apiEndpoint = isResumedAssessment ? 
        "get_resume_ai_assessment_batch_progress" : 
        "get_ai_assessment_batch_progress";

      console.log(`Calling ${apiEndpoint} with params:`, params);
      root.refs.API.request(
        apiEndpoint,
        params,
        getProgressOK,
        getProgressFailed
      );
    } else {
      console.error('Missing required params for fetchAssessmentProgress:', { 
        localeId, 
        submissionDate, 
        schoolId, 
        batchStartTime: effectiveBatchStartTime 
      });
    }
  };

  const startIndividualAssessment = (isResume = false) => {
    console.log(`Starting individual assessment process, isResume: ${isResume}`);
    
    // Extract answer IDs from answersDetail
    const allAnswerIds = answersDetail;
    
    if (isResume) {
      // For resumed assessments, we need to identify which answers have already been assessed
      // Use the actual submission date for API calls
      let dateParam = submissionDate;
      
      const params = {
        locale_id: localeId,
        date: dateParam,
        school_id: schoolId
      };
      
      // Get already assessed answer IDs
      root.refs.API.request(
        "get_total_assessed_answers_number_by_epack_and_date",
        params,
        (res) => {
          console.log('Assessed answers response:', res.body['result']);
          
          // Extract the assessed answer IDs if available
          let assessedAnswerIds = [];
          if (res.body['result'] && res.body['result'].answer_info && 
              res.body['result'].answer_info.answer_ids) {
            assessedAnswerIds = res.body['result'].answer_info.answer_ids;
            console.log('Already assessed answer IDs:', assessedAnswerIds);
          }
          
          // Filter out answers that have already been assessed
          const answersToProcess = allAnswerIds.filter(id => !assessedAnswerIds.includes(id));
          console.log(`${assessedAnswerIds.length} answers already assessed, processing remaining ${answersToProcess.length} answers`);
          
          if (answersToProcess.length === 0) {
            console.log('No answers left to assess');
            return;
          }
          
          // Process only the unevaluated answers
          processIndividualAssessment(answersToProcess, isResume);
        },
        (err) => {
          console.error('Failed to get assessed answer IDs:', err);
          // Fallback to processing all
          processIndividualAssessment(allAnswerIds, isResume);
        }
      );
    } else {
      // For new assessments, process all answer IDs
      processIndividualAssessment(allAnswerIds, isResume);
    }
  };

  const refreshAssessedAnswers = () => {
    // Reset the batch start time if assessment is complete
    if (progressDetails && progressDetails.is_complete) {
      setBatchStartTime(null);
    }
    
    if ((isDateRangeFilter || hasMultipleSelectedDates) && submissionList && submissionList.length > 0) {
      // For date ranges or multi-date selections, re-run aggregated totals instead of single-date API
      calculateTotalsFromSubmissionList();
      return;
    }

    if (localeId && submissionDate && schoolId) {
      // Use the actual submission date for API calls
      let dateParam = submissionDate;
      
      const params = {
        locale_id: localeId,
        date: dateParam,
        school_id: schoolId
      };

      root.refs.API.request(
        "get_total_assessed_answers_number_by_epack_and_date",
        params,
        getAssessedAnswersOK,
        getAssessedAnswersFailed
      );
    }
  };

  // The date(s) the manual-assessment button/count operate on: aggregate mode covers
  // every date in view, else the single viewed date.
  const getManualAssessmentDates = () => {
    if ((isDateRangeFilter || hasMultipleSelectedDates) && submissionList && submissionList.length > 0) {
      return (!isDateRangeFilter && hasMultipleSelectedDates)
        ? [...new Set(selectedDates.map(d => (typeof d === 'string' ? d.split(' ')[0] : null)).filter(Boolean))]
        : [...new Set(submissionList.map(s => (s.submission_time || '').split(' ')[0]).filter(Boolean))];
    }
    if (submissionDate) {
      return [String(submissionDate).split(' ')[0]];
    }
    return [];
  };

  // Fetch the accurate count of answers still needing AI assessment (covers voice kind 13
  // and every submission of the date, unlike the text-only get_total_* stats), summed across
  // the viewed date(s). Drives the Run AI Assessment button's disabled state.
  const refreshPendingManualCount = () => {
    // Both this count and the queueing route it feeds are `assert_epack_writable`
    // server-side, so on a locale another school authored they can only 403.
    if (!isManualAIAssessment || !canManageLocale) {
      return;
    }
    const dates = getManualAssessmentDates();
    if (!localeId || dates.length === 0) {
      setPendingManualCount(0);
      return;
    }
    let done = 0;
    let total = 0;
    let hadError = false;
    dates.forEach((date) => {
      root.refs.API.request(
        "count_pending_manual_ai_assessment_by_epack_and_date",
        { locale_id: localeId, date: date, school_id: schoolId },
        (res) => {
          const result = (res && res.body && res.body.result) || {};
          if (result.error) { hadError = true; } else { total += parseInt(result.pending, 10) || 0; }
          done++;
          if (done === dates.length && !isUnmountedRef.current) {
            // On error leave the count unknown (null) so the button falls back to the
            // text-answer stats instead of wrongly locking/unlocking.
            setPendingManualCount(hadError ? null : total);
          }
        },
        (err) => {
          console.error('count_pending_manual_ai_assessment_by_epack_and_date failed:', err);
          hadError = true;
          done++;
          if (done === dates.length && !isUnmountedRef.current) {
            setPendingManualCount(null);
          }
        }
      );
    });
  };

  // Manual AI assessment (epack.is_manual_ai_assessment = 1): queue the pending
  // answers of the viewed date(s) into ai_processing_queue. The backend cmd is
  // idempotent (answers already queued in a non-FAILURE state are skipped), so
  // re-clicking cannot double-queue.
  const runManualAIAssessment = () => {
    // Mirror the stats scope: aggregate mode queues every date in view, else the single date.
    const dates = getManualAssessmentDates();
    if (!localeId || dates.length === 0) {
      return;
    }

    setIsQueueingAssessment(true);
    let completed = 0;
    let queuedTotal = 0;
    let hadError = false;
    const finishOne = () => {
      completed++;
      if (completed < dates.length) {
        return;
      }
      if (!isUnmountedRef.current) {
        setIsQueueingAssessment(false);
        if (hadError) {
          root.addNotification(Localization.strings.AI_ASSESSMENT_QUEUED_FAIL, false);
        } else {
          root.addNotification(Localization.strings.AI_ASSESSMENT_QUEUED_OK.replace('{0}', queuedTotal), true);
        }
        refreshAssessedAnswers();
        refreshPendingManualCount();
      }
    };
    dates.forEach((date) => {
      root.refs.API.request(
        "queue_manual_ai_assessment_by_epack_and_date",
        { locale_id: localeId, date: date, school_id: schoolId },
        (res) => {
          const result = (res && res.body && res.body.result) || {};
          if (result.error) {
            hadError = true;
          } else {
            queuedTotal += parseInt(result.queued, 10) || 0;
          }
          finishOne();
        },
        (err) => {
          console.error('queue_manual_ai_assessment_by_epack_and_date failed:', err);
          hadError = true;
          finishOne();
        }
      );
    });
  };

  useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
      if (progressTimerRef.current) {
        clearTimeout(progressTimerRef.current);
        progressTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if ((isDateRangeFilter || hasMultipleSelectedDates) && submissionList && submissionList.length > 0) {
      // For date range filters or timeline multi-select, calculate totals from submissionList directly
      console.log('DEBUG: Calculating totals from submissionList for date range filter or multi-date selection');
      calculateTotalsFromSubmissionList();
    } else if (localeId && submissionDate && schoolId) {
      // For single date, use API calls as before
      console.log('DEBUG: Using API calls for single date');
      // Use the actual submission date for API calls
      let dateParam = submissionDate;
      
      const params = {
        locale_id: localeId,
        date: dateParam,
        school_id: schoolId
      };

      console.log('DEBUG: Making API call get_total_answers_number_by_epack_and_date with params:', params);

      root.refs.API.request(
        "get_total_answers_number_by_epack_and_date",
        params,
        getTotalAnswersOK,
        getTotalAnswersFailed
      );

      root.refs.API.request(
        "get_total_assessed_answers_number_by_epack_and_date",
        params,
        getAssessedAnswersOK,
        getAssessedAnswersFailed
      );
      
      // Calculate student count from submission list
      if (submissionList && submissionList.length > 0) {
        // Get unique student count - key on the student identifier (user_name), not the
        // per-submission id, so repeat submissions do not inflate the student count.
        const uniqueStudents = new Set(submissionList.map(submission => submission.user_name));
        setStudentCount(uniqueStudents.size);
      }
    }

    // Get count of assessable answers per student
    if (localeId) {
      const params = {
        epack_id: localeId
      };

      root.refs.API.request(
        "get_assessible_answers_count_by_epack_id",
        params,
        getQuestionsPerStudentOK,
        getQuestionsPerStudentFailed
      );
    }
  }, [localeId, submissionDate, schoolId, root, submissionList, isDateRangeFilter, hasMultipleSelectedDates, selectedDates]);
  
  // Reset all assessment statistics when submission list is cleared
  useEffect(() => {
    if (!submissionList || submissionList.length === 0) {
      console.log('DEBUG: Resetting assessment statistics due to empty submission list');
      setTotalAnswers(0);
      setAssessedAnswers(0);
      setStudentCount(0);
      setSubmissionsPerStudent(0);
      setQuestionsPerStudent(0);
      setAnswersDetail(null);
      setIsAssessmentInProgress(false);
      setProgressDetails(null);
      setBatchStartTime(null);
      setIsChartLoading(false);
      setIsPerformanceDataLoading(false);
      setHasScoreData(false);
      setHasPerformanceData(false);
      setTopPerformingQuestions([]);
      setLowPerformingQuestions([]);
      setTopPerformingStudents([]);
      setLowPerformingStudents([]);
    }
  }, [submissionList]);
  
  // Calculate questions per student: total questions to assess divided by student count
  useEffect(() => {
    if (studentCount > 0 && totalAnswers > 0) {
      setSubmissionsPerStudent(Math.round(totalAnswers / studentCount));
    }
  }, [totalAnswers, studentCount]);

  // Calculate progress percentage
  const progressPercentage = totalAnswers > 0 ? Math.round((assessedAnswers / totalAnswers) * 100) : 0;
  
  // Process individual answer assessment with retry logic
  const processIndividualAssessment = async (answerIds, isResume = false) => {
    if (!answerIds || answerIds.length === 0) {
      console.error('No answer IDs available for assessment');
      return;
    }
    
    console.log(`Starting individual assessment for ${answerIds.length} answers, isResume: ${isResume}`);
    setIsAssessmentInProgress(true);
    setIsResumedAssessment(isResume);
    
    // Local tracking variables instead of state updates
    let localSuccessCount = 0;
    let localFailureCount = 0;
    
    // Create a results object to track assessment results
    const results = {
      successful: [],
      failed: []
    };
    
    // Initial progress setup - we'll only update this for dialog display
    setProgressDetails({
      total_answers: answerIds.length,
      completed_successfully: 0,
      failed: 0,
      pending: answerIds.length,
      percentage_complete: 0,
      is_complete: false
    });
    
    // Process each answer ID
    for (let i = 0; i < answerIds.length; i++) {
      const answerId = answerIds[i];
      let success = false;
      let retryCount = 0;
      
      // Try up to 3 times for each answer
      while (!success && retryCount < 3) {
        try {
          await new Promise((resolve, reject) => {
            root.refs.API.request(
              "get_ai_assessment_v2",
              { answer_id: answerId },
              (res) => {
                const result = res?.body?.result;
                if (!result) {
                  console.error(`Malformed response assessing answer ${answerId}:`, res?.body);
                  reject(new Error('Malformed response'));
                  return;
                }
                if (result.error) {
                  console.error(`Error assessing answer ${answerId}:`, result.error);
                  reject(result.error);
                } else {
                  console.log(`Successfully assessed answer ${answerId}`);
                  success = true;
                  // Store the successful result if needed
                  results.successful.push({
                    answerId: answerId,
                    result: result.result
                  });
                  resolve(res);
                }
              },
              (err) => {
                console.error(`API error for answer ${answerId}:`, err);
                reject(err);
              }
            );
          });
        } catch (error) {
          retryCount++;
          console.log(`Retry ${retryCount}/3 for answer ${answerId}`);
          // Small delay before retry
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      // Track progress in local variables
      if (success) {
        localSuccessCount++;
      } else {
        localFailureCount++;
        // Track the failed answer ID
        results.failed.push(answerId);
      }
      
      // Only update dialog progress - not main UI state
      setProgressDetails(prev => {
        const completed = localSuccessCount;
        const failed = localFailureCount;
        const total = answerIds.length;
        const pending = total - completed - failed;
        const percentage = Math.round(((completed + failed) / total) * 100);
        
        return {
          total_answers: total,
          completed_successfully: completed,
          failed: failed,
          pending: pending,
          percentage_complete: percentage,
          is_complete: completed + failed >= total
        };
      });
    }
    
    // Assessment complete - now update all state variables at once
    setSuccessCount(localSuccessCount);
    setFailureCount(localFailureCount);
    setAssessedAnswers(prev => prev + localSuccessCount);
    
    // Final progress update
    setProgressDetails(prev => ({
      ...prev,
      is_complete: true,
      percentage_complete: 100,
      pending: 0
    }));
    
    // Assessment complete
    setIsAssessmentInProgress(false);
    console.log('Assessment complete', results);
    
    // Call refresh to update UI
    refreshAssessedAnswers();
    
    return results;
  };

  return (
    <Paper elevation={0} sx={{ p: 2 }}>
      <Dialog open={showNoQuestionsDialog} onClose={() => setShowNoQuestionsDialog(false)}>
        <DialogTitle>{"AI Assessment"}</DialogTitle>
        <DialogContent>
            <DialogContentText>
              {Localization.strings.NO_QUESTIONS_FOR_AI_ASSESSMENT || "There are no questions in this submission that are suitable for AI assessment."}
            </DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setShowNoQuestionsDialog(false)}>{Localization.strings.CLOSE || 'Close'}</Button>
        </DialogActions>
      </Dialog>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {/* Show "No answer submitted" message when no submissions exist */}
          {!hasSubmissions ? (
            <Card sx={{ p: 3, textAlign: 'center', mb: 3 }}>
              <Typography variant="body1" color="text.secondary">
                {Localization.strings.EMPTY_SUBMISSION_REMINDER}
              </Typography>
            </Card>
          ) : (
            <>
              {/* Selected Date Display and Date Filter - Horizontal Layout */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                flexWrap: 'wrap',
                gap: 2
              }}>
            {/* Left side - Date Display */}
            <Typography variant="h6" sx={{ 
              fontWeight: '600',
              //color: '#ad1179',
              fontSize: '1.1rem',
              flexShrink: 0
            }}>
              {Localization.strings.FIELD_TRIP_DATE || "實地考察日期"}: {getSelectedDateDisplay()}
                  </Typography>
                
            {/* Right side - Export + Date Filter - Horizontal */}
            {/* Wraps: three export buttons plus the optional Run button and the
                date filter overflow this row on a narrower window. */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1.5 }}>
              {isManualAIAssessment && canManageLocale && (() => {
                // Manual-assessment locales: teacher triggers queueing explicitly.
                // Prefer the accurate pending count (covers voice kind 13 + every submission);
                // fall back to the text-answer stats while it's unknown (endpoint not deployed,
                // errored, or still loading).
                const hasPending = pendingManualCount != null
                  ? pendingManualCount > 0
                  : (totalAnswers > 0 && assessedAnswers < totalAnswers);
                const allAssessed = !hasPending;
                const runButton = (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Icon fontSize="small">play_arrow</Icon>}
                    onClick={runManualAIAssessment}
                    disabled={allAssessed || isQueueingAssessment}
                    sx={{
                      textTransform: 'none',
                      backgroundColor: '#ad1179',
                      '&:hover': {
                        backgroundColor: '#8a0e63'
                      }
                    }}
                  >
                    {Localization.strings.RUN_AI_ASSESSMENT}
                  </Button>
                );
                return allAssessed ? (
                  <Tooltip title={Localization.strings.ALL_AI_QUESTIONS_COMPLETED}>
                    <span> {/* Span needed for Tooltip on a disabled Button */}
                      {runButton}
                    </span>
                  </Tooltip>
                ) : runButton;
              })()}
              {/* The two score exports sit ahead of the full answer export: they
                  are the quick reads, the answers file is the deep one. Each is
                  hidden when the locale has no figures of that kind. */}
              {localeHasAiScores && (
              <Button
                size="small"
                variant="contained"
                startIcon={<Icon fontSize="small">file_download</Icon>}
                onClick={() => onExportAiScores && onExportAiScores()}
                disabled={!canExportAnswers || !submissionList || submissionList.length === 0}
                sx={{
                  textTransform: 'none',
                  // AI_ACCENT -- the same magenta as the "AI Score" row on the
                  // student cards in the left menu (RetrieverPage.jsx).
                  backgroundColor: '#ad1179',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#8a0e63' }
                }}
              >
                {Localization.strings.EXPORT_ALL_AI_SCORES || 'Export all student AI Scores'}
              </Button>
              )}
              {localeHasMarks && (
              <Button
                size="small"
                variant="contained"
                startIcon={<Icon fontSize="small">file_download</Icon>}
                onClick={() => onExportMarks && onExportMarks()}
                disabled={!canExportAnswers || !submissionList || submissionList.length === 0}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#2e7d32',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#256628' }
                }}
              >
                {Localization.strings.EXPORT_ALL_MARKS || 'Export all student Marks'}
              </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={<Icon fontSize="small">file_download</Icon>}
                onClick={() => onExportAllAnswers && onExportAllAnswers()}
                disabled={!canExportAnswers || !submissionList || submissionList.length === 0}
                sx={{
                  textTransform: 'none',
                  borderColor: '#ad1179',
                  color: '#ad1179',
                  '&:hover': {
                    borderColor: '#8a0e63',
                    color: '#8a0e63',
                    backgroundColor: 'rgba(173,17,121,0.06)'
                  }
                }}
              >
                {Localization.strings.EXPORT_ALL_STUDENT_ANSWERS || Localization.strings.ARCHIVE || 'Export all student answers to Excel'}
              </Button>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                cursor: 'pointer',
                px: 1,
                py: 0.5,
                borderRadius: '4px',
                '&:hover': { bgcolor: 'action.hover' }
              }}
              onClick={() => setIsDateFilterExpanded(!isDateFilterExpanded)}
              >
                <Typography variant="body2" sx={{ mr: 1, color: '#ad1179', fontWeight: '500' }}>
                  {Localization.strings.DATE_RANGE || "Date Range"}
                  </Typography>
                <IconButton size="small" sx={{ 
                  transform: isDateFilterExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform 0.3s',
                  color: '#ad1179',
                  p: 0.5
                }}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
              
              {/* Horizontal Collapse/Expand for Date Filter Fields */}
              <Collapse in={isDateFilterExpanded} orientation="horizontal">
              <Box sx={{ 
                  ml: 1,
                  p: 1.5, 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '8px',
                  bgcolor: 'background.paper',
                display: 'flex', 
                alignItems: 'center', 
                  gap: 1.5,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  minWidth: 'fit-content'
                }}>
                  <TextField
                    type="date"
                    label={Localization.strings.FROM || "From"}
                    size="small"
                    value={tempDateFilterStart || ''}
                    onChange={(e) => onDateFilterStartChange && onDateFilterStartChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  sx={{ 
                      minWidth: '120px',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#ad1179',
                        },
                        '&:hover fieldset': {
                          borderColor: '#ad1179',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ad1179',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#ad1179',
                        '&.Mui-focused': {
                          color: '#ad1179',
                        },
                      },
                    }}
                  />
                  
                  <TextField
                    type="date"
                    label={Localization.strings.TO || "To"}
                    size="small"
                    value={tempDateFilterEnd || ''}
                    onChange={(e) => onDateFilterEndChange && onDateFilterEndChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ 
                      minWidth: '120px',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#ad1179',
                        },
                        '&:hover fieldset': {
                          borderColor: '#ad1179',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ad1179',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#ad1179',
                        '&.Mui-focused': {
                          color: '#ad1179',
                        },
                      },
                    }}
                  />
                  
                  <Button
                    size="small"
                    variant="contained"
                    onClick={onConfirmDateFilter}
                    sx={{ 
                      minWidth: 'fit-content',
                      backgroundColor: '#ad1179',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#8a0e63',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                      }
                    }}
                  >
                    {Localization.strings.CONFIRM || "Confirm"}
                  </Button>
                  
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onClearDateFilter}
                sx={{ 
                      minWidth: 'fit-content',
                      borderColor: '#ad1179',
                      color: '#ad1179',
                      '&:hover': {
                        backgroundColor: '#ad1179',
                        color: 'white',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    {Localization.strings.CLEAR || "Clear"}
                  </Button>
                </Box>
              </Collapse>
            </Box>
          </Box>
            </>
          )}
        </Grid>
        
        {/* Show AI Assessment content only when there are submissions */}
        {hasSubmissions && (
          <>
            {/* Merged Card - AI Assessment Statistics & Batch Assessment */}
            <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'visible', 
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)', 
            borderRadius: '8px' 
          }}>
            <Box sx={{ 
              bgcolor: '#9c27b0', 
              color: 'white', 
              py: 1.5, 
              px: 3, 
              borderRadius: '8px 8px 0 0',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Typography variant="h6" sx={{ mb: 0 }}>
                {isAIProgressNotification ? Localization.strings.AI_ASSESSMENT_STATISTICS || 'AI Assessment Statistics' : Localization.strings.ASSESSMENT_STATISTICS || 'Assessment Statistics'}
              </Typography>
            </Box>
            
            <CardContent sx={{ 
              py: 2, 
              flexGrow: 1, 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <Stack spacing={2}>
                {/* Basic Statistics */}
                <StatisticCard 
                  icon="people" 
                  title={Localization.strings.STUDENT_COUNT || "Number of Students"} 
                  value={studentCount} 
                />
                <StatisticCard 
                  icon="assignment" 
                  title={Localization.strings.QUESTIONS_PER_WORKSHEET || "Questions per Worksheet"} 
                  value={questionsPerStudent} 
                />
                
                {/* Assessment Progress Statistics */}
                {isAIProgressNotification ? <><StatisticCard 
                  icon="analytics" 
                  title={Localization.strings.AI_ASSESSABLE_QUESTIONS || "AI Assessable Questions"} 
                  value={totalAnswers} 
                />
                <StatisticCard 
                  icon="schedule" 
                  title={Localization.strings.ONGOING_AI_ASSESSABLE_QUESTIONS || "Ongoing AI-Assessable Questions"} 
                  value={totalAnswers - assessedAnswers} 
                />
                <StatisticCard 
                  icon="check_circle" 
                  title={Localization.strings.AI_ASSESSED_QUESTIONS || "AI Assessed Questions"} 
                  value={assessedAnswers} 
                />
                <StatisticCard 
                  icon="percent" 
                  title={Localization.strings.COMPLETION_PERCENTAGE || "Completion Percentage"} 
                  value={progressPercentage} 
                  suffix="%" 
                /></> : null}
              </Stack>
              
              {/* Assessment action buttons */}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Second Card - Assessment Summary with Score Distribution Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ 
                bgcolor: '#9c27b0', 
                color: 'white', 
                py: 1.5, 
                mb: 3,
                borderRadius: '8px 8px 0 0'
              }}>
                <Typography variant="h6" component="div" align="center">
                  {Localization.strings.SCORE_DISTRIBUTION || 'Score Distribution'}
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                px: 3,
                flexGrow: 1,
                justifyContent: 'center'
              }}>
                {/* The 分數分佈 heading that used to sit here is now the card
                    title, so the tab strip is the first thing under it. */}

                {/* Chart view switcher — always all three, so a view that is
                    empty today is still reachable. Only the default selection
                    follows the data (see activeChartTab). */}
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={activeChartTab}
                  onChange={(e, value) => { if (value !== null) setChartTab(value); }}
                  sx={{
                    mb: 2,
                    alignSelf: 'center',
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.8rem',
                      color: '#666',
                      borderColor: '#ddd',
                    },
                    '& .MuiToggleButton-root.Mui-selected': {
                      backgroundColor: '#ad1179',
                      color: '#fff',
                      '&:hover': { backgroundColor: '#8e0e63' },
                    },
                  }}
                >
                  <ToggleButton value={CHART_TAB_SCORE}>
                    {Localization.strings.CHART_TAB_SCORE || 'AI Score'}
                  </ToggleButton>
                  <ToggleButton value={CHART_TAB_PAPER}>
                    {Localization.strings.CHART_TAB_PAPER_SCORE || 'Paper Score'}
                  </ToggleButton>
                  <ToggleButton value={CHART_TAB_TOPIC}>
                    {Localization.strings.CHART_TAB_BY_TOPIC || 'By Content Type'}
                  </ToggleButton>
                </ToggleButtonGroup>

                {/* A bare "1" over a bar does not say whether it counts students
                    or answers — and the two differ by exactly the number of
                    questions per student, which reads as a wrong number next to
                    the 可AI評估問題 tile. Say which the bars are counting. */}
                {activeHasData && (
                  <Typography variant="caption" sx={{ color: '#888', mb: 1 }}>
                    {isTopicTab
                      ? (Localization.strings.BAR_UNIT_ANSWERS || 'Each bar: number of answers')
                      : (Localization.strings.BAR_UNIT_STUDENTS || 'Each bar: number of students')}
                  </Typography>
                )}

                {/* Chart container - enhanced */}
                <Box sx={{ 
                  width: '100%', 
                  height: '340px', 
                  display: 'flex', 
                  flexDirection: 'column',
                  justifyContent: 'flex-end', 
                  alignItems: 'center',
                  position: 'relative',
                  mb: 2,
                  px: 1
                }}>
                  {isChartLoading ? (
                    <Box sx={{ 
                      position: 'absolute', 
                      top: '40%', 
                      left: '50%', 
                      transform: 'translate(-50%, -50%)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center' 
                    }}>
                      <CircularProgress size={40} sx={{ color: '#ad1179' }} />
                      <Typography variant="body2" sx={{ mt: 2, color: '#666' }}>
                        {Localization.strings.LOADING_DATA || 'Loading data...'}
                      </Typography>
                    </Box>
                  ) : !activeHasData ? (
                    <Box sx={{ 
                      position: 'absolute', 
                      top: '40%', 
                      left: '50%', 
                      transform: 'translate(-50%, -50%)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      p: 3,
                      bgcolor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px dashed #ddd'
                    }}>
                      <Icon sx={{ fontSize: '3rem', color: '#bdbdbd', mb: 2 }}>bar_chart</Icon>
                      <Typography variant="body1" color="text.secondary" align="center">
                        {Localization.strings.NO_DATA}<br/>
                        <Typography variant="caption" color="text.secondary">
                          {isPaperTab
                            ? (canShowPaperScore
                                ? (Localization.strings.NO_PAPER_SCORE_DATA
                                    || 'No student has a marked answer yet')
                                : (Localization.strings.SCORE_NOT_SET_TIP
                                    || 'No question in this LOCALE has marks assigned, so there is no score'))
                            : (Localization.strings.COMPLETE_ASSESSMENT_FOR_DATA
                                || 'Complete assessment to view score distribution')}
                        </Typography>
                      </Typography>
                    </Box>
                  ) : (
                    // Enhanced score distribution chart
                    <Box sx={{ 
                      width: '100%', 
                      height: '260px', 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-end',
                      mt: 'auto',
                      mb: 2,
                      px: 2,
                      position: 'relative'
                    }}>
                      {/* Y-axis line */}
                      <Box sx={{
                        position: 'absolute',
                        left: 0,
                        bottom: 0,
                        width: '1px',
                        height: '220px',
                        bgcolor: '#e0e0e0'
                      }}/>
                      
                      {/* X-axis line */}
                      <Box sx={{
                        position: 'absolute',
                        left: 0,
                        bottom: 0,
                        width: '100%',
                        height: '1px',
                        bgcolor: '#e0e0e0'
                      }}/>
                      
                      {activeBuckets.map((count, index) => (
                        <Tooltip
                          key={index}
                          title={activeBarTooltip(index)}
                          placement="top"
                          arrow
                          // The names inside are clickable, so the tip has to
                          // survive the mouse crossing the arrow gap to reach it.
                          leaveDelay={200}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: '#fff',
                                color: '#333',
                                border: '1px solid #ccc',
                                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                maxWidth: 'none',
                                whiteSpace: 'pre-line',
                                fontSize: '0.75rem',
                                padding: '8px 12px'
                              }
                            }
                          }}
                        >
                          <Box
                            sx={{
                              width: activeBarWidth,
                              height: count > 0 ? `${Math.max(20, (count / activeAxisMax) * 220)}px` : '1px',
                              backgroundColor: getActiveBarColor(index),
                              borderRadius: '3px 3px 0 0',
                              position: 'relative',
                              transition: 'all 0.3s ease',
                              border: count === 0 ? '1px dashed #ccc' : 'none',
                              borderBottom: 'none',
                              '&:hover': {
                                opacity: 0.8,
                                transform: 'translateY(-4px) scaleY(1.03)',
                                cursor: 'pointer',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                              },
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'flex-start',
                              alignItems: 'center',
                            }}
                          >
                            {count > 0 && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  position: 'absolute', 
                                  top: '-20px', 
                                  fontSize: '0.8rem',
                                  fontWeight: 'bold',
                                  color: '#333',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {count}
                              </Typography>
                            )}
                            {count === 0 && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  position: 'absolute', 
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  fontSize: '0.7rem',
                                  color: '#999'
                                }}
                              >
                                0
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                      ))}
                    </Box>
                  )}
                  
                  {/* X-axis labels — same width formula as the bars above, or the
                      two independent flex rows drift apart. */}
                  {activeHasData &&
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                      mt: 1,
                      px: 2
                    }}>
                      {activeLabels.map((label, index) => (
                        <Typography key={index} variant="caption" align="center" sx={{
                          width: activeBarWidth,
                          fontSize: '0.7rem',
                          color: '#666',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: isTopicTab ? 'normal' : 'nowrap',
                          lineHeight: 1.2
                        }}>
                          {label}
                        </Typography>
                      ))}
                    </Box>
                  }
                </Box>

                {/* X-axis title */}
                {activeHasData && (
                  <Typography variant="body2" sx={{ mt: 0, mb: 2, color: '#666', fontSize: '0.8rem' }}>
                    {isPaperTab
                      ? `${Localization.strings.PAPER_SCORE_MARKS || 'Marks'} (0-${paperMaxMarks})`
                      : (Localization.strings.SCORE_PERCENT || 'Score (%)')}
                  </Typography>
                )}

                {/* Average for whichever view is on screen — the pill must describe
                    the chart above it, not a different metric. */}
                {activeHasData && (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mt: 2,
                    p: 2,
                    bgcolor: '#f8f9fa',
                    borderRadius: '8px'
                  }}>
                    <Typography variant="body1" color="text.secondary">
                      {Localization.strings.AVERAGE_SCORE || 'Average Score'}:
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#ad1179' }}>
                      {isPaperTab
                        ? `${paperAverageMarks}/${paperMaxMarks}`
                        : (isTopicTab ? `${averageScore}%` : `${studentAverageScore}%`)}
                    </Typography>
                  </Box>
                )}
                
                {/* Refresh button */}
                {assessedAnswers > 0 && (
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    size="medium" 
                    onClick={fetchAllAIAssessments}
                    disabled={isChartLoading}
                    sx={{ 
                      mt: 3,
                      px: 3,
                      borderRadius: '20px',
                      fontWeight: 'normal',
                      borderColor: '#ad1179',
                      color: '#ad1179',
                      '&:hover': {
                        borderColor: '#9c27b0',
                        bgcolor: 'rgba(173, 17, 121, 0.04)'
                      }
                    }}
                    startIcon={<RefreshIcon />}
                  >
                    {isChartLoading ? 
                      Localization.strings.REFRESHING || 'Refreshing...' : 
                      Localization.strings.REFRESH_DATA || 'Refresh Data'}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* New Card - Performance Summary */}
        <Grid item xs={12}>
          <Card sx={{ 
            width: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ 
                bgcolor: '#9c27b0', 
                color: 'white', 
                py: 1.5, 
                mb: 3,
                borderRadius: '8px 8px 0 0'
              }}>
                <Typography variant="h6" component="div" align="center">
                  {Localization.strings.PERFORMANCE_SUMMARY || 'Performance Summary'}
                </Typography>
              </Box>
              
              <Box sx={{ px: 2 }}>
                {isPerformanceDataLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      {Localization.strings.LOADING_DATA || 'Loading data...'}
                    </Typography>
                  </Box>
                ) : !hasPerformanceData ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <Typography variant="body1">
                      {Localization.strings.NO_PERFORMANCE_DATA || 'No performance data available yet. Complete AI assessments to view student performance.'}
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    {/* Average Score Display */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      mb: 4,
                      flexDirection: 'column' 
                    }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, color: '#666' }}>
                        {Localization.strings.AVERAGE_SCORE || 'Average Score'}
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        width: '150px',
                        height: '150px',
                        borderRadius: '50%',
                        bgcolor: '#FFA500', // Brighter orange to match screenshot
                        color: 'white',
                        position: 'relative',
                        mb: 2
                      }}>
                        <Typography 
                          variant="h2" 
                          sx={{ 
                            fontWeight: 'bold',
                            fontSize: '2.7rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {averageScore}%
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Performance Metrics Grid */}
                    <Grid container spacing={3}>
                      {/* Top Performing Questions */}
                      <Grid item xs={12} md={6}>
                        <Paper elevation={0} sx={{ p: 0, mb: 4, overflow: 'hidden' }}>
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              p: 1.5, 
                              pl: 2,
                              bgcolor: '#e8f5e9', // Light green background
                              color: '#2e7d32', 
                              borderTop: '1px solid #c8e6c9',
                              borderBottom: '1px solid #c8e6c9'
                            }}
                          >
                            {Localization.strings.BEST_PERFORMANCE_QUESTIONS || 'Best Performance Questions'}
                          </Typography>
                          {topPerformingQuestions.length > 0 ? (
                            <List dense disablePadding>
                              {topPerformingQuestions.map((question, index) => (
                                <ListItem 
                                  key={index} 
                                  sx={{ 
                                    py: 1.5,
                                    px: 2,
                                    borderBottom: '1px solid #f0f0f0'
                                  }}
                                >
                                  {/* `secondary` below is a <Box> with <Typography> children; MUI wraps
                                      `secondary` in a <p> by default, which makes that invalid DOM
                                      nesting (React warns, and the browser splits the paragraph). */}
                                  <ListItemText 
                                    secondaryTypographyProps={{ component: 'div' }}
                                    primary={
                                      <Tooltip title={question.fullText} placement="top">
                                        <Typography variant="body2" noWrap>
                                          {index + 1}. {question.text}
                                        </Typography>
                                      </Tooltip>
                                    }
                                    secondary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            color: '#4caf50', 
                                            fontWeight: 'bold'
                                          }}
                                        >
                                          {question.average}%
                                        </Typography>
                                        <Typography variant="caption" sx={{ ml: 1, color: '#666' }}>
                                          ({question.count} {Localization.strings.ANSWERS || 'answers'})
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <Typography variant="body2" align="center" sx={{ color: '#666', mt: 2, p: 2 }}>
                              {Localization.strings.NO_DATA || 'No data available'}
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                      
                      {/* Top Performing Students */}
                      <Grid item xs={12} md={6}>
                        <Paper elevation={0} sx={{ p: 0, mb: 4, overflow: 'hidden' }}>
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              p: 1.5, 
                              pl: 2,
                              bgcolor: '#e8f5e9', // Light green background
                              color: '#2e7d32', 
                              borderTop: '1px solid #c8e6c9',
                              borderBottom: '1px solid #c8e6c9'
                            }}
                          >
                            {Localization.strings.BEST_PERFORMANCE_STUDENTS || 'Best Performance Students'}
                          </Typography>
                          {topPerformingStudents.length > 0 ? (
                            <List dense disablePadding>
                              {topPerformingStudents.map((student, index) => (
                                <ListItem 
                                  key={index} 
                                  sx={{ 
                                    py: 1.5,
                                    px: 2,
                                    borderBottom: '1px solid #f0f0f0'
                                  }}
                                >
                                  {/* `secondary` below is a <Box> with <Typography> children; MUI wraps
                                      `secondary` in a <p> by default, which makes that invalid DOM
                                      nesting (React warns, and the browser splits the paragraph). */}
                                  <ListItemText 
                                    secondaryTypographyProps={{ component: 'div' }}
                                    primary={
                                      <Typography variant="body2" fontWeight="medium">{student.name}</Typography>
                                    }
                                    secondary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            color: '#4caf50', 
                                            fontWeight: 'bold'
                                          }}
                                        >
                                          {student.average}%
                                        </Typography>
                                        <Typography variant="caption" sx={{ ml: 1, color: '#666' }}>
                                          ({student.count} {Localization.strings.ANSWERS || 'answers'})
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <Typography variant="body2" align="center" sx={{ color: '#666', mt: 2, p: 2 }}>
                              {Localization.strings.NO_DATA || 'No data available'}
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                      
                      {/* Low Performing Questions */}
                      <Grid item xs={12} md={6}>
                        <Paper elevation={0} sx={{ p: 0, overflow: 'hidden' }}>
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              p: 1.5, 
                              pl: 2,
                              bgcolor: '#ffebee', // Light red background
                              color: '#c62828', 
                              borderTop: '1px solid #ffcdd2',
                              borderBottom: '1px solid #ffcdd2'
                            }}
                          >
                            {Localization.strings.WORST_PERFORMANCE_QUESTIONS || 'Worst Performance Questions'}
                          </Typography>
                          {lowPerformingQuestions.length > 0 ? (
                            <List dense disablePadding>
                              {lowPerformingQuestions.map((question, index) => (
                                <ListItem 
                                  key={index} 
                                  sx={{ 
                                    py: 1.5,
                                    px: 2,
                                    borderBottom: '1px solid #f0f0f0'
                                  }}
                                >
                                  {/* `secondary` below is a <Box> with <Typography> children; MUI wraps
                                      `secondary` in a <p> by default, which makes that invalid DOM
                                      nesting (React warns, and the browser splits the paragraph). */}
                                  <ListItemText 
                                    secondaryTypographyProps={{ component: 'div' }}
                                    primary={
                                      <Tooltip title={question.fullText} placement="top">
                                        <Typography variant="body2" noWrap>
                                          {index + 1}. {question.text}
                                        </Typography>
                                      </Tooltip>
                                    }
                                    secondary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            color: '#f44336', 
                                            fontWeight: 'bold'
                                          }}
                                        >
                                          {question.average}%
                                        </Typography>
                                        <Typography variant="caption" sx={{ ml: 1, color: '#666' }}>
                                          ({question.count} {Localization.strings.ANSWERS || 'answers'})
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <Typography variant="body2" align="center" sx={{ color: '#666', mt: 2, p: 2 }}>
                              {Localization.strings.NO_DATA || 'No data available'}
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                      
                      {/* Low Performing Students */}
                      <Grid item xs={12} md={6}>
                        <Paper elevation={0} sx={{ p: 0, overflow: 'hidden' }}>
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              p: 1.5, 
                              pl: 2,
                              bgcolor: '#ffebee', // Light red background
                              color: '#c62828', 
                              borderTop: '1px solid #ffcdd2',
                              borderBottom: '1px solid #ffcdd2'
                            }}
                          >
                            {Localization.strings.WORST_PERFORMANCE_STUDENTS || 'Worst Performance Students'}
                          </Typography>
                          {lowPerformingStudents.length > 0 ? (
                            <List dense disablePadding>
                              {lowPerformingStudents.map((student, index) => (
                                <ListItem 
                                  key={index} 
                                  sx={{ 
                                    py: 1.5,
                                    px: 2,
                                    borderBottom: '1px solid #f0f0f0'
                                  }}
                                >
                                  {/* `secondary` below is a <Box> with <Typography> children; MUI wraps
                                      `secondary` in a <p> by default, which makes that invalid DOM
                                      nesting (React warns, and the browser splits the paragraph). */}
                                  <ListItemText 
                                    secondaryTypographyProps={{ component: 'div' }}
                                    primary={
                                      <Typography variant="body2" fontWeight="medium">{student.name}</Typography>
                                    }
                                    secondary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            color: '#f44336', 
                                            fontWeight: 'bold'
                                          }}
                                        >
                                          {student.average}%
                                        </Typography>
                                        <Typography variant="caption" sx={{ ml: 1, color: '#666' }}>
                                          ({student.count} {Localization.strings.ANSWERS || 'answers'})
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <Typography variant="body2" align="center" sx={{ color: '#666', mt: 2, p: 2 }}>
                              {Localization.strings.NO_DATA || 'No data available'}
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                    </Grid>
                    
                    {/* Refresh button for performance data */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                      <Button 
                        variant="contained" 
                        size="small" 
                        onClick={fetchAllAIAssessments}
                        disabled={isPerformanceDataLoading}
                        sx={{ 
                          borderRadius: '20px',
                          bgcolor: '#9c27b0',
                          color: 'white',
                          px: 2.5,
                          '&:hover': {
                            bgcolor: '#7b1fa2'
                          }
                        }}
                        startIcon={<RefreshIcon />}
                      >
                        {isPerformanceDataLoading ? 
                          Localization.strings.REFRESHING || 'Refreshing...' : 
                          Localization.strings.REFRESH_DATA || 'Refresh Data'}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
          </>
        )}
      </Grid>
    </Paper>
  );
};

export default AIAssessmentPage;
